import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { FaArrowLeft, FaPlay, FaTimes, FaSearch, FaLock } from 'react-icons/fa';
import VideoPlayer from '@/components/VideoPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: extraer file_id de una URL de Google Drive
const getDriveFileId = (url) => {
  if (!url || !url.includes('drive.google.com')) return null;
  if (url.includes('/file/d/')) return url.split('/file/d/')[1]?.split('/')[0]?.split('?')[0] || null;
  if (url.includes('id=')) return url.split('id=')[1]?.split('&')[0] || null;
  return null;
};

// Helper: detectar URLs de Cloudinary (reproducción HTML5 nativa, sin iframe)
// Cloudinary sirve `video/mp4` + `Accept-Ranges: bytes` con moov al inicio
// (fast-start) — funciona perfecto en iPhone, Android y desktop sin proxy.
const isCloudinaryUrl = (url) => {
  if (!url) return false;
  return /res\.cloudinary\.com\/.+\/video\/upload\//i.test(url);
};

// Helper: detectar URLs de Sirv (reproducción HTML5 nativa, alta calidad).
// Sirv sirve mp4/mov con fast-start + byte-range → reproduce en cualquier
// dispositivo sin proxy ni iframe.
const isSirvUrl = (url) => {
  if (!url) return false;
  return /\.sirv\.com\//i.test(url);
};

// 2026-02 · Bunny Stream / Bunny CDN
const isBunnyPlayerUrl = (url) => !!url && /(player|iframe)\.mediadelivery\.net\/(play|embed)\//i.test(url);
const isBunnyDirectUrl = (url) => !!url && /\.b-cdn\.net\//i.test(url);

// Helper: detectar iPhone/iPad/iPod (Safari incluido)
// En iOS el iframe /preview de Drive suele reproducir SOLO audio; usamos
// <video> HTML5 con URL directa de descarga de Drive.
const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isIPadOS = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  return isIOS || isIPadOS;
};

// Función para convertir URLs a embebibles
const getEmbedUrl = (url, source) => {
  if (!url) return null;
  
  // YouTube
  if (source === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = '';
    if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    }
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
  }
  
  // Google Drive - formato preview
  if (source === 'drive' || url.includes('drive.google.com')) {
    let fileId = '';
    
    if (url.includes('/file/d/')) {
      fileId = url.split('/file/d/')[1]?.split('/')[0]?.split('?')[0];
    } else if (url.includes('id=')) {
      fileId = url.split('id=')[1]?.split('&')[0];
    }
    
    if (fileId) {
      return `https://drive.google.com/file/d/${fileId}/preview`;
    }
  }
  
  return url;
};

// Obtener thumbnail de YouTube
const getYouTubeThumbnail = (url) => {
  if (!url) return null;
  let videoId = '';
  if (url.includes('youtube.com/watch')) {
    const urlParams = new URLSearchParams(url.split('?')[1]);
    videoId = urlParams.get('v');
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  } else if (url.includes('youtube.com/embed/')) {
    videoId = url.split('embed/')[1]?.split('?')[0];
  }
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  }
  return null;
};

// Verificar si necesita iframe
const needsIframe = (url, source) => {
  return source === 'youtube' || source === 'drive' ||
         url?.includes('youtube.com') || url?.includes('youtu.be') ||
         url?.includes('drive.google.com');
};

// Categorías/filtros disponibles
const CATEGORIES = [
  { id: 'all', name: 'Todos' },
  { id: 'free', name: 'Gratis' },
  { id: 'videos_entrenamiento', name: 'Entrenamiento' },
  { id: 'videos_explicativos', name: 'Explicativos' },
  { id: 'premium', name: 'Premium' }
];

const Videos = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('title-asc'); // Ordenamiento
  // 2026-02 · Categorías personalizadas creadas desde el panel admin.
  // Se mezclan con las fijas para pintar chips extra en el landing.
  const [customCategories, setCustomCategories] = useState([]);

  useEffect(() => {
    fetchVideos();
    fetchCustomCategories();
  }, []);

  const fetchCustomCategories = async () => {
    try {
      const res = await axios.get(`${API}/video-categories`);
      setCustomCategories(res.data?.items || []);
    } catch (error) {
      setCustomCategories([]);
    }
  };

  const fetchVideos = async () => {
    try {
      // Enviar token si el usuario está autenticado para que el backend determine el acceso
      const token = localStorage.getItem('deisycfit_token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(`${API}/videos`, config);
      
      if (response.data.length === 0) {
        // Videos de ejemplo
        setVideos([
          {
            id: '1',
            title: 'Técnica de Sentadillas',
            description: 'Aprende la forma correcta para realizar sentadillas',
            category: 'nivel_1',
            duration: '10:30',
            is_free: true,
            can_access: true,
            video_url: 'https://www.youtube.com/watch?v=example1',
            thumbnail_url: 'https://images.unsplash.com/photo-1566241142559-40e1dab266c6?w=800&h=450&fit=crop'
          },
          {
            id: '2',
            title: 'Rutina de Abs Completa',
            description: 'Ejercicios efectivos para fortalecer el core',
            category: 'nivel_2',
            duration: '15:00',
            is_free: false,
            can_access: false,
            video_url: 'https://www.youtube.com/watch?v=example2',
            thumbnail_url: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&h=450&fit=crop'
          }
        ]);
      } else {
        setVideos(response.data);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayVideo = (video) => {
    // Solo permite reproducir si can_access es true (determinado por el backend)
    if (video.can_access === true) {
      setSelectedVideo(video);
    }
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  // Filtrar videos
  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          video.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeFilter === 'all') return matchesSearch;
    if (activeFilter === 'free') return matchesSearch && video.is_free === true;
    if (activeFilter === 'premium') return matchesSearch && !video.is_free;
    return matchesSearch && video.category === activeFilter;
  });

  // Ordenar videos
  const sortedVideos = [...filteredVideos].sort((a, b) => {
    const [field, order] = sortBy.split('-');
    let aVal, bVal;
    
    if (field === 'title') {
      aVal = a.title?.toLowerCase() || '';
      bVal = b.title?.toLowerCase() || '';
    } else if (field === 'date') {
      aVal = new Date(a.created_at || 0).getTime();
      bVal = new Date(b.created_at || 0).getTime();
    } else if (field === 'category') {
      aVal = a.category?.toLowerCase() || '';
      bVal = b.category?.toLowerCase() || '';
    }
    
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });

  // Obtener thumbnail del video
  const getThumbnail = (video) => {
    if (video.thumbnail_url) return video.thumbnail_url;
    
    // Intentar obtener thumbnail de YouTube
    const ytThumb = getYouTubeThumbnail(video.video_url);
    if (ytThumb) return ytThumb;
    
    // Placeholder por defecto
    return 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&h=450&fit=crop';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div 
          className="w-16 h-16 rounded-full animate-pulse"
          style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
        />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-6 px-4 md:px-6 relative"
      style={{ backgroundColor: '#0a0a0a' }}
      data-testid="videos-page"
    >
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.08), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.06), transparent 40%)'
        }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header con búsqueda */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')} 
            className="text-gray-400 hover:text-white shrink-0" 
            data-testid="back-button"
          >
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          
          {/* Barra de búsqueda */}
          <div className="flex-1 max-w-md relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              type="text"
              placeholder="Buscar contenido..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-full"
            />
          </div>
        </div>

        {/* Filtros y Ordenamiento */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {[
              ...CATEGORIES,
              ...customCategories
                .filter((c) => c?.slug && !CATEGORIES.some((f) => f.id === c.slug))
                .map((c) => ({
                  id: c.slug,
                  name: `${c.emoji ? c.emoji + ' ' : ''}${c.label}`,
                })),
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeFilter === cat.id
                    ? 'text-white'
                    : 'text-gray-400 hover:text-white bg-white/5 hover:bg-white/10'
                }`}
                style={activeFilter === cat.id ? { 
                  background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary))' 
                } : {}}
              >
                {cat.name}
              </button>
            ))}
          </div>
          
          {/* Selector de Ordenamiento */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 rounded-full text-sm bg-white/5 border border-white/10 text-gray-300 focus:border-pink-500 focus:outline-none"
          >
            <option value="title-asc">📝 Nombre (A-Z)</option>
            <option value="title-desc">📝 Nombre (Z-A)</option>
            <option value="date-desc">📅 Más recientes</option>
            <option value="date-asc">📅 Más antiguos</option>
            <option value="category-asc">📁 Categoría (A-Z)</option>
          </select>
        </div>

        {/* Banner de desbloqueo si hay videos bloqueados */}
        {sortedVideos.some(v => v.can_access !== true) && (
          <div 
            className="mb-6 p-4 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4"
            style={{ 
              background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.2), rgba(168, 85, 247, 0.2))',
              border: '1px solid rgba(var(--brand-primary-glow), 0.3)'
            }}
            data-testid="unlock-banner"
          >
            <div className="flex items-center gap-3">
              <FaLock className="text-pink-400 text-xl" />
              <div>
                <h3 className="text-white font-bold">DESBLOQUEA TODA LA BIBLIOTECA</h3>
                <p className="text-gray-300 text-sm">Accede a todos los videos con una membresía o compra acceso individual</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/plans')}
                className="font-bold text-white"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), #FF69B4)' }}
                data-testid="view-memberships-btn"
              >
                VER MEMBRESÍAS
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/plans')}
                className="font-bold border-white/30 text-white hover:bg-white/10"
                data-testid="buy-access-btn"
              >
                COMPRAR ACCESO
              </Button>
            </div>
          </div>
        )}

        {/* Videos Grid - Estilo Netflix/Modern */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {sortedVideos.map((video, idx) => {
            // El acceso se determina en el backend basado en: is_free, allowed_plans y el plan del usuario
            const canPlay = video.can_access === true;
            const thumbnail = getThumbnail(video);
            
            return (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <div 
                  className="group relative rounded-xl overflow-hidden cursor-pointer"
                  style={{ aspectRatio: '16/9' }}
                  onClick={() => handlePlayVideo(video)}
                  data-testid={`video-card-${video.id}`}
                >
                  {/* Thumbnail/Cover - con blur si está bloqueado */}
                  <img 
                    src={thumbnail}
                    alt={video.title}
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                      canPlay ? 'group-hover:scale-110' : 'blur-sm scale-105'
                    }`}
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&h=450&fit=crop';
                    }}
                  />
                  
                  {/* Overlay gradient */}
                  <div 
                    className="absolute inset-0 transition-opacity duration-300"
                    style={{
                      background: canPlay 
                        ? 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)'
                        : 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.5) 100%)'
                    }}
                  />
                  
                  {/* Badge "Video" o "PREMIUM" */}
                  <div 
                    className="absolute top-3 right-3 px-3 py-1 rounded text-xs font-bold text-white"
                    style={{ background: canPlay ? 'var(--brand-primary)' : 'rgba(100,100,100,0.9)' }}
                  >
                    {canPlay ? 'Video' : 'PREMIUM'}
                  </div>
                  
                  {/* Lock icon prominente si está bloqueado - estilo premium */}
                  {!canPlay && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-2"
                        style={{ 
                          background: 'rgba(0,0,0,0.7)',
                          border: '2px solid rgba(255,255,255,0.2)'
                        }}
                      >
                        <FaLock className="text-white text-2xl" />
                      </div>
                      <span className="text-white/80 text-sm font-medium">PREMIUM</span>
                    </div>
                  )}
                  
                  {/* Duración */}
                  {video.duration && (
                    <div 
                      className="absolute bottom-16 right-3 px-2 py-0.5 rounded text-xs text-white font-medium"
                      style={{ background: 'rgba(0,0,0,0.8)' }}
                    >
                      {video.duration}
                    </div>
                  )}
                  
                  {/* Play button overlay (visible on hover) */}
                  {canPlay && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform duration-300"
                        style={{ 
                          background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
                          boxShadow: '0 0 30px rgba(var(--brand-primary-glow), 0.5)'
                        }}
                      >
                        <FaPlay className="text-white text-xl ml-1" />
                      </div>
                    </div>
                  )}
                  
                  {/* Contenido inferior */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 
                      className="text-white font-semibold text-base mb-1 line-clamp-1"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {video.title}
                    </h3>
                    <p className="text-gray-300 text-sm line-clamp-2 opacity-80">
                      {video.description}
                    </p>
                    
                    {/* Badges */}
                    <div className="flex items-center gap-2 mt-2">
                      {video.level && (
                        <span className="text-xs px-2 py-0.5 rounded-full border border-white/20 text-white/70">
                          {video.level}
                        </span>
                      )}
                      {video.is_free && (
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#00F0FF20', color: '#00F0FF' }}>
                          Gratis
                        </span>
                      )}
                    </div>
                    
                    {/* Botón de acción */}
                    <div className="mt-3">
                      <Button 
                        className="w-full rounded-full font-bold text-sm h-10"
                        style={{ 
                          background: canPlay ? 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' : 'linear-gradient(135deg, rgba(var(--brand-primary-glow),0.8), rgba(168,85,247,0.8))',
                          color: 'white'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canPlay) {
                            handlePlayVideo(video);
                          } else {
                            navigate('/plans');
                          }
                        }}
                        data-testid={canPlay ? `play-video-${video.id}` : `unlock-video-${video.id}`}
                      >
                        {canPlay ? (
                          <>
                            <FaPlay className="mr-2 text-xs" /> 
                            VER AHORA
                          </>
                        ) : (
                          <>
                            <FaLock className="mr-2 text-xs" /> 
                            DESBLOQUEAR
                          </>
                        )}
                      </Button>
                      {!canPlay && (
                        <p className="text-center text-gray-400 text-xs mt-2">
                          Activa una membresía para acceder
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Empty state */}
        {filteredVideos.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No se encontraron videos</p>
            <Button 
              variant="ghost" 
              onClick={() => { setSearchTerm(''); setActiveFilter('all'); }}
              className="mt-4 text-pink-500"
            >
              Limpiar filtros
            </Button>
          </div>
        )}

        {/* CTA para no autenticados */}
        {!isAuthenticated && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-16"
          >
            <div 
              className="rounded-2xl p-10 text-center"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              <h3 
                className="text-2xl md:text-3xl font-bold text-white mb-3 uppercase"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                ¿Quieres acceso completo?
              </h3>
              <p className="text-white/90 mb-6">Regístrate y obtén acceso a todos los videos premium</p>
              <Button 
                size="lg" 
                onClick={() => navigate('/#planes')} 
                className="rounded-full px-8 py-5 font-bold uppercase tracking-wider bg-white text-black hover:bg-gray-100"
                data-testid="cta-plans-button"
              >
                Ver Planes
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Modal de Reproductor */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0, 0, 0, 0.95)' }}
            onClick={closeVideoModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-5xl rounded-2xl overflow-hidden"
              style={{ background: '#0a0a0a' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header — flujo normal (no absolute) para no tapar los controles */}
              <div
                className="flex items-center justify-between px-4 py-3 shrink-0"
                style={{ background: '#0a0a0a', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                data-testid="video-modal-header"
              >
                <h3 className="text-white font-bold text-base sm:text-lg truncate pr-4">{selectedVideo.title}</h3>
                <button
                  onClick={closeVideoModal}
                  className="p-2 rounded-full transition-colors hover:bg-white/20 shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.1)' }}
                  data-testid="video-modal-close-btn"
                >
                  <FaTimes className="text-white text-xl" />
                </button>
              </div>

              {/* Reproductor — altura fija en móvil (50vh) y aspecto 16:9 en desktop */}
              <div
                className="relative w-full bg-black h-[50vh] sm:h-auto sm:pt-[56.25%]"
                data-testid="video-modal-player"
              >
                <VideoPlayer
                  url={selectedVideo.video_url}
                  source={selectedVideo.video_source}
                  title={selectedVideo.title}
                />
              </div>

              {/* Descripción */}
              <div className="p-4 max-h-32 overflow-y-auto" style={{ background: '#111' }}>
                <p className="text-gray-300 text-sm">{selectedVideo.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Videos;
